// Search by photo — the browser side. The photo is checked, shrunk to at most
// 1024 px and re-encoded as JPEG here (which also drops its EXIF data, such as
// where it was taken) before anything is sent; the server checks it again.
// The descriptor (src/lib/imageDescriptor.js) is sent with it for the local
// reference provider; a learned provider ignores it.
import { descriptorFromRgba, rgbaFromImage } from './imageDescriptor';
import { marketSourceSearchFn } from './marketDb';

export const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
export const MAX_INPUT_BYTES = 15 * 1024 * 1024;
const MAX_SIDE = 1024;
const STORE_KEY = 'mk_image_search';

/** → { dataUrl, descriptor } or { error: 'type' | 'size' | 'unreadable' } */
export async function prepareSearchImage(file) {
  if (!file) return { error: 'unreadable' };
  if (file.type && !ACCEPTED.includes(file.type.toLowerCase())) return { error: 'type' };
  if (file.size > MAX_INPUT_BYTES) return { error: 'size' };
  let bmp;
  try { bmp = await createImageBitmap(file); } catch { return { error: 'unreadable' }; }
  const scale = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bmp.width * scale); canvas.height = Math.round(bmp.height * scale);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
  const descriptor = descriptorFromRgba(rgbaFromImage(canvas, canvas.width, canvas.height, document));
  bmp.close?.();
  return { dataUrl, descriptor };
}

/** Hand a prepared photo to the results page (a navigation away, same tab). */
export function stashSearchImage(prep) {
  try { sessionStorage.setItem(STORE_KEY, JSON.stringify(prep)); return true; } catch { return false; }
}
/** Taken ONCE: a second read (React's development double-mount, a reload) finds
 *  nothing, so one photo is never searched — and charged against the limits — twice. */
export function takeSearchImage() {
  try { const v = sessionStorage.getItem(STORE_KEY); sessionStorage.removeItem(STORE_KEY); return v ? JSON.parse(v) : null; } catch { return null; }
}

/** Photo search on HioBuy's sources (market-source-search) → { ok, results: [{ref, title_ar, title_en, price_cny}], error } */
export async function searchByImage(prep) {
  const r = await marketSourceSearchFn({ action: 'search', image: prep.dataUrl });
  return r.ok ? { ok: true, results: r.results || [] } : { ok: false, error: r.error || 'failed', status: r.httpStatus };
}

/** The photo read as the words the store's OWN catalog is searched with (owner,
 *  2026-10-25: our products first, then the suppliers'). Sent beside searchByImage,
 *  answers in a second or two → { ok, ar, en } | { ok: false, error }. A server
 *  without the action answers invalid_request: the page shows the suppliers' results alone. */
export async function describeImage(prep) {
  const r = await marketSourceSearchFn({ action: 'describe', image: prep.dataUrl });
  return r.ok ? { ok: true, ar: r.q_ar || null, en: r.q_en || null } : { ok: false, error: r.error || 'failed' };
}

/** Text search on HioBuy's sources (market-source-search), one page ("show more" asks for the next)
 *  → { ok, results: [{ref, title_ar, title_en, price_cny}], more, error } */
export async function searchSourcesByText(q, page = 1) {
  const r = await marketSourceSearchFn({ action: 'search_text', q, page });
  return r.ok ? { ok: true, results: r.results || [], more: !!r.more } : { ok: false, error: r.error || 'failed', status: r.httpStatus };
}

/** Open a result: the product id (imported on demand when needed) or { error: 'not_available' | 'busy' | … }. */
export async function openSourceResult(ref) {
  const r = await marketSourceSearchFn({ action: 'open', ref });
  return r.ok ? { ok: true, productId: r.product_id } : { ok: false, error: r.error || 'failed' };
}
